import * as THREE from 'three';
import { toonMat, createToonGradientMap } from './materials.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LobbyPortal {
  id: string;
  label: string;
  position: THREE.Vector3;
}

// ---------------------------------------------------------------------------
// Internal portal state
// ---------------------------------------------------------------------------

interface PortalEntry {
  portal: LobbyPortal;
  group: THREE.Group;
  innerDisc: THREE.Mesh;
  discMat: THREE.MeshBasicMaterial;
  phaseOffset: number;
}

interface NpcEntry {
  group: THREE.Group;
  baseY: number;
  phaseOffset: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PORTAL_NEARBY_RADIUS = 3;

const PORTAL_DEFS: Array<{ id: string; label: string; x: number; z: number; color: number }> = [
  { id: 'solo',      label: 'Solo Showdown',    x:   0, z: -30, color: 0x44aaff },
  { id: 'duos',      label: 'Duos',             x: -20, z: -20, color: 0x44ff88 },
  { id: 'squads',    label: 'Squads',            x:  20, z: -20, color: 0xff8844 },
  { id: '50v50',     label: '50v50',             x: -35, z:   0, color: 0xff4488 },
  { id: 'zone_wars', label: 'Zone Wars',         x:  35, z:   0, color: 0xaa44ff },
];

const NPC_POSITIONS: Array<[number, number, number]> = [
  [-10, 0, 10],
  [  0, 0, 15],
  [ 10, 0, 10],
];

// ---------------------------------------------------------------------------
// FpsLobbyIsland
// ---------------------------------------------------------------------------

export class FpsLobbyIsland {
  private readonly scene: THREE.Scene;
  private readonly grad: THREE.DataTexture;

  private readonly objects: THREE.Object3D[] = [];
  private readonly portals: PortalEntry[] = [];
  private readonly npcs: NpcEntry[] = [];

  private readonly labelEl: HTMLDivElement;
  private time = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.grad = createToonGradientMap();

    this._ensureLights();
    this._buildGround();
    this._buildPortals();
    this._buildNpcs();
    this._buildKiosk();

    // DOM label
    this.labelEl = document.createElement('div');
    this.labelEl.style.cssText = [
      'position:fixed',
      'bottom:60px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:rgba(0,0,0,0.55)',
      'color:#fff',
      'font-family:sans-serif',
      'font-size:18px',
      'padding:8px 22px',
      'border-radius:8px',
      'z-index:7000',
      'pointer-events:none',
      'display:none',
      'letter-spacing:0.04em',
    ].join(';');
    document.body.appendChild(this.labelEl);
  }

  // ---------------------------------------------------------------------------
  // update
  // ---------------------------------------------------------------------------

  update(dt: number): void {
    this.time += dt;
    const t = this.time;

    // Spin portal groups + pulse inner disc opacity
    for (const pe of this.portals) {
      pe.group.rotation.y += dt * 0.6;
      const opacity = 0.4 + 0.4 * (0.5 + 0.5 * Math.sin(t * Math.PI + pe.phaseOffset));
      pe.discMat.opacity = opacity;
    }

    // Bob NPCs
    for (const npc of this.npcs) {
      const bob = 0.2 * Math.sin((t / 1.0) * Math.PI + npc.phaseOffset); // 2s period → π/1
      npc.group.position.y = npc.baseY + bob;
    }
  }

  // ---------------------------------------------------------------------------
  // getNearbyPortal
  // ---------------------------------------------------------------------------

  getNearbyPortal(playerPos: THREE.Vector3): LobbyPortal | null {
    for (const pe of this.portals) {
      const dx = playerPos.x - pe.portal.position.x;
      const dz = playerPos.z - pe.portal.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist <= PORTAL_NEARBY_RADIUS) {
        return pe.portal;
      }
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // showHUD / hideHUD helpers (called externally or from update)
  // ---------------------------------------------------------------------------

  showPortalLabel(label: string): void {
    this.labelEl.textContent = `[ E ]  Enter ${label}`;
    this.labelEl.style.display = 'block';
  }

  hidePortalLabel(): void {
    this.labelEl.style.display = 'none';
  }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    for (const obj of this.objects) {
      this.scene.remove(obj);
    }
    this.objects.length = 0;
    this.portals.length = 0;
    this.npcs.length = 0;
    this.labelEl.remove();
  }

  // ---------------------------------------------------------------------------
  // Private builders
  // ---------------------------------------------------------------------------

  private _add(obj: THREE.Object3D): void {
    this.scene.add(obj);
    this.objects.push(obj);
  }

  private _ensureLights(): void {
    let lightCount = 0;
    this.scene.traverse((child) => {
      if (child instanceof THREE.Light) lightCount++;
    });
    if (lightCount < 2) {
      const ambient = new THREE.AmbientLight(0xffffff, 0.55);
      this._add(ambient);

      const dir = new THREE.DirectionalLight(0xffffff, 1.2);
      dir.position.set(30, 60, 20);
      dir.castShadow = true;
      this._add(dir);
    }
  }

  private _buildGround(): void {
    const geo = new THREE.PlaneGeometry(200, 200);
    const mat = toonMat(0x5ab552, this.grad);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.receiveShadow = true;
    this._add(mesh);
  }

  private _buildPortals(): void {
    for (let i = 0; i < PORTAL_DEFS.length; i++) {
      const def = PORTAL_DEFS[i];
      const group = new THREE.Group();
      group.position.set(def.x, 3, def.z); // raise so torus base sits above ground

      // Outer torus ring
      const torusMat = toonMat(def.color, this.grad, { emissive: def.color, emissiveIntensity: 0.4 });
      const torus = new THREE.Mesh(new THREE.TorusGeometry(3, 0.4, 8, 32), torusMat);
      group.add(torus);

      // Inner pulsing disc
      const discMat = new THREE.MeshBasicMaterial({
        color: def.color,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      });
      const innerDisc = new THREE.Mesh(new THREE.CircleGeometry(2.9, 32), discMat);
      group.add(innerDisc);

      this._add(group);

      const portalData: LobbyPortal = {
        id: def.id,
        label: def.label,
        position: new THREE.Vector3(def.x, 0, def.z),
      };

      this.portals.push({
        portal: portalData,
        group,
        innerDisc,
        discMat,
        phaseOffset: (i / PORTAL_DEFS.length) * Math.PI * 2,
      });
    }
  }

  private _buildNpcs(): void {
    for (let i = 0; i < NPC_POSITIONS.length; i++) {
      const [x, , z] = NPC_POSITIONS[i];
      const group = new THREE.Group();
      group.position.set(x, 0, z);

      // Body (box)
      const bodyMat = toonMat(0x3366cc, this.grad);
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.9, 0.4), bodyMat);
      body.position.y = 0.9;
      body.castShadow = true;
      group.add(body);

      // Head (sphere)
      const headMat = toonMat(0xffcc99, this.grad);
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.28, 8, 8), headMat);
      head.position.y = 1.63;
      head.castShadow = true;
      group.add(head);

      this._add(group);
      this.npcs.push({
        group,
        baseY: 0,
        phaseOffset: (i / NPC_POSITIONS.length) * Math.PI * 2,
      });
    }
  }

  private _buildKiosk(): void {
    const group = new THREE.Group();
    group.position.set(0, 0, 20);

    // Main kiosk box
    const bodyMat = toonMat(0xee9900, this.grad, { emissive: 0xcc7700, emissiveIntensity: 0.3 });
    const body = new THREE.Mesh(new THREE.BoxGeometry(2, 3, 1), bodyMat);
    body.position.y = 1.5;
    body.castShadow = true;
    group.add(body);

    // Sign on top (contrasting color box)
    const signMat = toonMat(0xff2255, this.grad, { emissive: 0xff0033, emissiveIntensity: 0.5 });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.5, 0.2), signMat);
    sign.position.y = 3.25;
    group.add(sign);

    this._add(group);
  }
}
